import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Avatar,
  CardHeader,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import { validateEmail } from "@utils/string";
import {
  OrganisationLineItem,
  createDefaultOrganisation,
} from "@interfaces/organisation";
import { debounce } from "lodash";

interface OrganisationFinderProps {
  id: string;
  name: string;
  label: string;
  existingOrganisation?: OrganisationLineItem;
  disabled: boolean;
  error?: boolean;
  helperText?: string;
  handleSearch: (query: string) => Promise<OrganisationLineItem[]>;
  onChange: (value?: OrganisationLineItem) => void;
  onBlur: (e: React.ChangeEvent<any>) => void;
}

export const OrganisationFinder = ({
  id,
  name,
  label,
  disabled,
  existingOrganisation,
  error,
  helperText,
  handleSearch,
  onChange,
  onBlur,
}: OrganisationFinderProps) => {
  const [isSearching, setIsSeaching] = React.useState(false);

  const [value, setValue] = React.useState<OrganisationLineItem | undefined>(
    existingOrganisation
  );
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<OrganisationLineItem[]>([]);

  const runSearch = async () => {
    setIsSeaching(true);
    setOptions([]);
    const searchResults = await handleSearch(inputValue);
    if (searchResults.length > 0) {
      setOptions(searchResults);
    } else {
      const match = validateEmail(inputValue);
      if (match) {
        setOptions([createDefaultOrganisation(inputValue)]);
      }
    }
    setIsSeaching(false);
  };
  const debouncedSearch = debounce(runSearch, 500);

  React.useEffect(() => {
    if (inputValue.length > 3) {
      debouncedSearch();
    }
  }, [value, inputValue]);

  return (
    <Autocomplete
      id={id}
      fullWidth
      loading={isSearching}
      disabled={disabled}
      getOptionLabel={(option) =>
        option.organisationName.length === 0
          ? `Invite - ${option.representativeEmail}`
          : `${option.organisationName}`
      }
      filterOptions={(x) => x}
      filterSelectedOptions
      options={options}
      value={value}
      noOptionsText="Enter email to invite user."
      onChange={(event: any, newValue: OrganisationLineItem | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue || undefined);
        onChange(newValue || undefined);
      }}
      onBlur={onBlur}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          name={name}
          label={label}
          fullWidth
          margin="dense"
          error={error}
          helperText={helperText}
          // InputProps={{
          //   startAdornment: value ? (
          //     <InputAdornment position="start">
          //       <Avatar
          //         src={value.organisationLogo}
          //         alt={value.organisationName[0].toUpperCase()}
          //       />
          //     </InputAdornment>
          //   ) : undefined,
          // }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props}>
            <Grid container alignItems="center">
              {option.organisationName.length > 0 ? (
                <Grid item sx={{ display: "flex" }}>
                  <CardHeader
                    titleTypographyProps={{
                      fontWeight: "25px",
                      fontSize: "5px",
                      padding: "0px !important",
                    }}
                    avatar={
                      <Avatar
                        src={option.organisationLogo}
                        alt={option.organisationName[0].toUpperCase()}
                      />
                    }
                    title={option.organisationName}
                  />
                </Grid>
              ) : (
                <Grid item sx={{ wordWrap: "break-word" }}>
                  <Typography color="text.secondary">
                    {option.representativeEmail}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </li>
        );
      }}
    />
  );
};
